import { Component, Input } from '@angular/core';
import * as Highcharts from 'highcharts';
import { reportMapOptions } from '../highcharts-options';
import { QuickBuildService, UIFilter } from 'src/app/people/quick-build.service';
import { PeopleReportService } from 'src/app/people/report-service.service';
import { BaseReportComponent, ChartContextMenuItem } from '../base-report.component';
import { Log } from 'src/app/logger.service';
import { AuthService } from 'src/app/auth/service/auth.service';
import { StatsigService } from '@statsig/angular-bindings';
import { WorkforceLayoutService } from 'src/app/workforce/workforce-layout.service';

var us_map = require('@highcharts/map-collection/countries/us/us-all.geo.json');
var world_map = require('@highcharts/map-collection/custom/world-robinson.geo.json');
import 'highcharts/modules/map';
import { StreamsSubscribeDrawerService } from '../../streams-subscribe-drawer/streams-subscribe-drawer.service';
import { ToggleOption } from '../../components/option-toggle/option-toggle.component';
import { SearchFilter } from 'ldt-people-api';
import { LocationMapConfig } from '../interfaces/report-config.interfaces';

const REPORT_NAME = 'demographics';

@Component({
    selector: 'app-reports-location-map',
    templateUrl: './location-map.component.html',
    styleUrls: ['./location-map.component.scss'],
    standalone: false
})
export class LocationMapComponent extends BaseReportComponent<LocationMapConfig> {
  @Input() boxShadow: boolean = true;

  viewToggleOptions: ToggleOption[] = [
    { value: 'location_details.region_abbreviation', displayText: 'US' },
    { value: 'location_details.country_code', displayText: 'World' },
  ];

  @Input()
  set selectedView(value: string | undefined) {
    if (value) {
      this._selectedView = value;
    }
  }
  get selectedView(): string {
    return this._selectedView;
  }

  private _selectedView: string = 'location_details.country_code';

  protected readonly CHART_ID = 'map-person-location';

  constructor(
    reportService: PeopleReportService,
    quickBuildService: QuickBuildService,
    streamsDrawerService: StreamsSubscribeDrawerService,
    auth: AuthService,
    statsigService: StatsigService,
    layoutService: WorkforceLayoutService
  ) {
    super(
      reportService,
      quickBuildService,
      streamsDrawerService,
      auth,
      statsigService,
      layoutService
    );

    // Register configuration properties for automatic emission
    this.registerConfigProperty('selectedView', () => this._selectedView);
  }

  protected initializeChart(): void {
    this.chart = Highcharts.mapChart(
      `highchartsContainer-${this.chartId}`,
      Highcharts.merge(reportMapOptions)
    );
    this.chart.update(
      {
        title: { text: 'Active Employee Worldwide Distribution' },
        yAxis: { title: { text: '% of employees' } },
      },
      true,
      true
    );

    this.initializeContextMenu();
  }

  protected getChartData(): void {
    if (!this.quickBuild) return;

    this.showLoading();

    const reportParams = {
      group_by: [this._selectedView],
    };

    // For US chart, filter on US to get better % values relative to US employees only
    const qbCopy = QuickBuildService.copyQuickBuild(this.quickBuild);
    if (this._selectedView === 'location_details.region_abbreviation') {
      const locationFilter: UIFilter = {
        filter: {
          field: 'location_details.country_code',
          match_type: SearchFilter.MatchTypeEnum.Exact,
          type: SearchFilter.TypeEnum.Must,
          string_values: ['us'],
        },
      };
      QuickBuildService.addFiltersToFilterGroup(qbCopy, [locationFilter], '__root__');
    }
    const sub = this.reportService
      .getReportDataWithReportDetails(
        this.reportId,
        REPORT_NAME,
        reportParams,
        this.reportTarget,
        qbCopy
      )
      .subscribe({
        next: (data) => {
          Log.d('MapUsStatesComponent getChartData data', data);
          const [series] = this.generateChartSeries(
            data.count,
            data.data.demographics,
            this._selectedView
          );
          this.updateChart(
            series,
            undefined,
            this._selectedView === 'location_details.region_abbreviation'
              ? 'Active Employee US Distribution'
              : 'Active Employee Worldwide Distribution'
          );
          Log.d('LocationMapComponent getChartData series', this.chart);
          this.chartLoadingComplete();
        },
        error: (error) => {
          Log.e('LocationMapComponent getChartData error', error);
          this.showChartError(error);
        },
      });
    this.subscriptions.add(sub);
  }

  protected generateChartSeries(total: number, data: any[], key: string): any[] {
    Log.d('MapUsStatesComponent generateChartSeries', total, data);
    const counts = data
      .map((d) => {
        let ret = { state: d.group_values[0].value, count: d.count_employees };
        return ret;
      })
      .reduce((r: any, i: any) => ((r[i.state] = (r[i.state] || 0) + i.count), r), {});
    const totalPoints: any = Object.values(counts).reduce((a: any, b: any) => a + b, 0);
    let processed: any[] = [];
    if (totalPoints) {
      processed = Object.keys(counts).map((k: any) => {
        const locationKey =
          key === 'location_details.region_abbreviation'
            ? 'us-' + k.toLowerCase()
            : k.toLowerCase();
        return [locationKey, counts[k]];
      });
    }

    if (processed.length === 0) {
      return [];
    }

    return [
      {
        name: 'Number of employees',
        type: 'map',
        mapData: this._selectedView === 'location_details.region_abbreviation' ? us_map : world_map,
        dataLabels: {
          enabled: true,
          format: '{point.name}',
        },
        data: processed,
      },
    ];
  }

  protected getContextMenuItems(point: Highcharts.Point): ChartContextMenuItem[] {
    const value = (point as any)['hc-key'];
    const series = point.series;
    const category = point.name as string;
    if (!this.quickBuild) {
      Log.s('LocationMapComponent getContextMenuItems', 'error', {
        error: 'No quick build found',
      });
      return [];
    }

    const qbCopy = QuickBuildService.copyQuickBuild(this.quickBuild);

    const searchValue =
      this._selectedView === 'location_details.region_abbreviation'
        ? value.split('-')[1].toUpperCase()
        : value;

    const categoryFilter: UIFilter = {
      filter: {
        field: this._selectedView,
        match_type: SearchFilter.MatchTypeEnum.Exact,
        type: SearchFilter.TypeEnum.Must,
        string_values: [searchValue],
      },
    };
    Log.d('LocationMapComponent getContextMenuItems categoryFilter', categoryFilter);

    QuickBuildService.addFiltersToFilterGroup(qbCopy, [categoryFilter], '__root__');

    return [
      {
        label: `View ${category} employees that work at ${series.name}`,
        icon: 'group',
        action: (p) => {
          const group = QuickBuildService.findFilterGroupById('moneyball', qbCopy.rootFilterGroup);
          group!.isJobsGroup = true;
          group!.jobsGroupType = 'active';
          qbCopy.name = `${category} employees at ${series.name}`;
          this.openDrawer(qbCopy);
        },
      },
    ];
  }

  onViewChanged(value: string) {
    if (!value) return;

    this._selectedView = value;
    this.emitConfiguration();
    this.resetChartData();
    this.getChartData();
  }
}
